import React from 'react';
import { Link } from 'react-router-dom';
import type { Client } from '../../services/crmService';
import { normalizeCrmLeadStatus } from '../../services/crmService';
import { getDisplayLeadScore, getDisplayLeadTemperature } from '../../utils/leadScore';
import { nextBestActionForPipelineClient } from '../../utils/nextBestLeadAction';
import LeadTemperatureBadge from './LeadTemperatureBadge';
import { DncBadge } from '../crm/DncBadge';
import DashboardCard from './DashboardCard';

interface RankedPipelineListProps {
  title: string;
  viewAllTo: string;
  viewAllLabel?: string;
  clients: Client[];
  loading: boolean;
  emptyTitle: string;
  emptyDescription: string;
  emptyCtaLabel: string;
  emptyCtaTo: string;
  /** Show CRM workflow chip for lead-stage queue */
  variant?: 'lead' | 'prospect';
  /** Show one-line conversion guidance from score + workflow */
  showNextAction?: boolean;
  /** When set, "View" and the name open this URL (e.g. deep link to Leads profile). */
  detailHrefForClient?: (c: Client) => string;
  /** Grid span / layout classes only. */
  className?: string;
}

function sourceLabel(source: string | undefined): string {
  if (!source) return 'Unknown source';
  return source.replace(/_/g, ' ');
}

const WORKFLOW_LABEL: Record<string, string> = {
  new: 'New',
  attempted: 'Attempted',
  contacted: 'Contacted',
  replied: 'Replied',
  qualified: 'Qualified',
  unqualified: 'Unqualified',
};

function workflowLabel(status: string | null | undefined): string {
  if (!status) return '';
  const n = normalizeCrmLeadStatus(status);
  return WORKFLOW_LABEL[n] || status;
}

const RankedPipelineList: React.FC<RankedPipelineListProps> = ({
  title,
  viewAllTo,
  viewAllLabel = 'View all',
  clients,
  loading,
  emptyTitle,
  emptyDescription,
  emptyCtaLabel,
  emptyCtaTo,
  variant = 'lead',
  showNextAction = false,
  detailHrefForClient,
  className,
}) => {
  return (
    <DashboardCard
      title={title}
      action={
        <Link to={viewAllTo} className="text-sm font-medium text-primary hover:text-primary-dark shrink-0">
          {viewAllLabel}
        </Link>
      }
      className={className}
      scrollBody
    >
        {loading ? (
          <div className="flex items-center justify-center min-h-36 text-fg-muted text-sm">
            Loading…
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-8 px-4 rounded-md border border-dashed border-border bg-app/50">
            <p className="text-sm font-medium text-fg">{emptyTitle}</p>
            <p className="text-sm text-fg-secondary mt-2 max-w-sm mx-auto">{emptyDescription}</p>
            <Link
              to={emptyCtaTo}
              className="inline-block mt-4 btn-primary text-sm py-2 px-4 rounded"
            >
              {emptyCtaLabel}
            </Link>
          </div>
        ) : (
          <ul className="space-y-3">
            {clients.map((c) => {
              const score = getDisplayLeadScore(c);
              const temperature = getDisplayLeadTemperature(c);
              const wf = variant === 'lead' ? workflowLabel(c.lead_workflow_status) : '';
              const detailTo = detailHrefForClient?.(c);
              return (
                <li key={c.id}>
                  <div
                    className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-md border hover:bg-app/80 transition-colors ${
                      c.do_not_contact
                        ? 'border-amber-300/50 bg-amber-50/80 dark:bg-amber-950/20 dark:border-amber-800/40'
                        : 'border-border'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {detailTo ? (
                          <Link to={detailTo} className="font-medium text-fg truncate hover:text-primary">
                            {c.display_name || c.full_name}
                          </Link>
                        ) : (
                          <span className="font-medium text-fg truncate">{c.display_name || c.full_name}</span>
                        )}
                        {c.do_not_contact ? <DncBadge /> : null}
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface border border-border text-fg-secondary capitalize">
                          {sourceLabel(c.source)}
                        </span>
                        {wf ? (
                          <span className="text-xs text-fg-muted border border-border rounded px-2 py-0.5">
                            {wf}
                          </span>
                        ) : null}
                      </div>
                      <p className="text-sm text-fg-muted truncate mt-0.5">
                        {c.company_name || c.email}
                      </p>
                      {showNextAction ? (
                        <p className="text-xs text-fg-secondary mt-1.5 line-clamp-2">
                          <span className="font-medium text-fg-muted">Next: </span>
                          {variant === 'prospect'
                            ? 'Follow up on your next step and deal value—move to Client when the sale is won.'
                            : nextBestActionForPipelineClient(c)}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-3 shrink-0 flex-wrap sm:justify-end">
                      <div className="flex items-center gap-2">
                        <LeadTemperatureBadge temperature={temperature} />
                        <span className="text-sm font-semibold text-fg tabular-nums" title="0–100 score">
                          {score}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Link
                          to={detailTo ?? viewAllTo}
                          className="font-medium text-primary hover:underline"
                        >
                          View
                        </Link>
                        {c.email ? (
                          <a
                            href={`mailto:${encodeURIComponent(c.email)}`}
                            className="text-fg-secondary hover:text-primary"
                          >
                            Email
                          </a>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
    </DashboardCard>
  );
};

export default RankedPipelineList;
